import { FC, type ReactNode } from 'react';

import { tableStore } from '../../stores/table-store';
import {
  DECISIONS_MODEL,
  GATEWAY_DEFAULT_MODEL,
  GATEWAY_MODELS,
  gatewayModel,
} from '../../utils/gateway-models';
import {
  stepTitle,
  type ModelRow,
  type SupervisorRouting,
} from './agent-models';
import type { AgentKey } from './agent-settings-model';

/**
 * Agent settings' Model section: the model each of an agent's AI steps calls
 * through the AI Gateway, and for a Supervisor with a CallAIClassifier step,
 * whether it routes with OpenAI Decisions. Edits go into the panel's draft;
 * Save writes them (`agent-settings-panel.tsx`).
 */

const PROVIDERS = [...new Set(GATEWAY_MODELS.map((m) => m.provider))];

const defaultLabel = `Gateway default (${
  gatewayModel(GATEWAY_DEFAULT_MODEL)?.label ?? GATEWAY_DEFAULT_MODEL
})`;

const ModelSelect: FC<{
  id: string;
  value: string;
  disabled: boolean;
  onChange: (model: string) => void;
}> = ({ id, value, disabled, onChange }) => {
  const known =
    value === 'default' || GATEWAY_MODELS.some((m) => m.id === value);
  return (
    <select
      id={id}
      className="c-agent-model__select"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="default">{defaultLabel}</option>
      {!known && <option value={value}>{value}</option>}
      {PROVIDERS.map((provider) => (
        <optgroup key={provider} label={provider}>
          {GATEWAY_MODELS.filter((m) => m.provider === provider).map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
              {m.textOnly ? ' (text only)' : ''}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
};

const StepHead: FC<{ row: ModelRow; htmlFor: string; title?: string }> = ({
  row,
  htmlFor,
  title,
}) => (
  <div className="c-agent-model__head">
    <label htmlFor={htmlFor}>{title ?? stepTitle(row)}</label>
    <button
      type="button"
      className="c-agent-settings__link"
      onClick={() => tableStore.openNodeOnCanvas(row.id)}
    >
      Step #{row.nodeNumber}
    </button>
  </div>
);

/** After a model or routing save: tests run on Sandbox, so say where to publish. */
export const ModelSaved: FC<{
  draftVersion: string | null;
  onDeploy: () => void;
}> = ({ draftVersion, onDeploy }) => (
  <div className="c-agent-model__saved" data-testid="bobcat-agent-model-saved">
    Saved to {draftVersion ?? 'the draft'}. Publish it from{' '}
    <button type="button" className="c-agent-settings__link" onClick={onDeploy}>
      Deploy
    </button>{' '}
    to test it.
  </div>
);

export const AgentModelSection: FC<{
  agent: AgentKey;
  steps: ModelRow[];
  routing: SupervisorRouting<ModelRow> | null;
  models: Record<string, string>;
  decisions: boolean | null;
  isViewer: boolean;
  onModel: (rowId: string, model: string) => void;
  onDecisions: (on: boolean) => void;
  /** Below the cards: what a just-saved change did, when there is one. */
  footer?: ReactNode;
}> = ({
  agent,
  steps,
  routing,
  models,
  decisions,
  isViewer,
  onModel,
  onDecisions,
  footer,
}) => {
  if (!steps.length) {
    return (
      <p className="c-agent-settings__none">
        This agent has no AI step that goes through the AI Gateway.
      </p>
    );
  }
  const routed = agent === 'supervisor' && routing;
  const others = steps.filter(
    (s) => !routed || (s !== routing.classifier && s !== routing.fallback)
  );
  const fallbackLabel = routed
    ? (gatewayModel(models[routing.fallback.id] ?? 'default')?.label ??
      models[routing.fallback.id])
    : '';
  return (
    <div className="c-agent-model" data-testid={`bobcat-agent-model-${agent}`}>
      {routed && (
        <div className="c-agent-model__card">
          <StepHead
            row={routing.classifier}
            htmlFor="agent-model-routing"
            title="Routing"
          />
          <select
            id="agent-model-routing"
            className="c-agent-model__select"
            data-testid="bobcat-agent-model-routing"
            value={decisions ? 'decisions' : 'model'}
            disabled={isViewer}
            onChange={(e) => onDecisions(e.target.value === 'decisions')}
          >
            <option value="decisions">{DECISIONS_MODEL.label}</option>
            <option value="model">{fallbackLabel}</option>
          </select>
          <hr />
          <StepHead
            row={routing.fallback}
            htmlFor="agent-model-fallback"
            title={decisions ? 'Fallback' : 'Routing model'}
          />
          <ModelSelect
            id="agent-model-fallback"
            value={models[routing.fallback.id] ?? 'default'}
            disabled={isViewer}
            onChange={(m) => onModel(routing.fallback.id, m)}
          />
        </div>
      )}
      {others.length > 0 && (
        <div className="c-agent-model__card">
          {others.map((row, i) => (
            <div key={row.id} className="c-agent-model__step">
              {i > 0 && <hr />}
              <StepHead row={row} htmlFor={`agent-model-${row.id}`} />
              <ModelSelect
                id={`agent-model-${row.id}`}
                value={models[row.id] ?? 'default'}
                disabled={isViewer}
                onChange={(m) => onModel(row.id, m)}
              />
            </div>
          ))}
        </div>
      )}
      {footer}
    </div>
  );
};
